import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const FeeReportsByClass = () => {
  const today = new Date();
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [currentSession] = useState(storedSession);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("paid");

  const printRef = useRef();

  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  /* ================= FETCH CLASSES ================= */
  useEffect(() => {
    if (!currentSession) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`, {
        params: { sessionId: currentSession }
      })
      .then(res => setClasses(res.data))
      .catch(console.error);
  }, [currentSession]);

  /* ================= FETCH SECTIONS ================= */
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`,
        { params: { sessionId: currentSession } }
      )
      .then(res => setSections(res.data || []))
      .catch(console.error);
  }, [selectedClass, currentSession]);

  /* ================= FETCH STUDENTS ================= */
  useEffect(() => {
    if (!currentSession) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/fees/report/students`, {
        params: {
          sessionId: currentSession,
          year: selectedYear,
          month: selectedMonth,
          class: selectedClass || undefined,
          section: selectedSection || undefined
        }
      })
      .then(res => setStudents(res.data || []))
      .catch(console.error);
  }, [currentSession, selectedMonth, selectedYear, selectedClass, selectedSection]);

  /* ================= FILTER STUDENTS ================= */
useEffect(() => {
  const filtered = students.filter(s => {
    const paid = s.paidAmount || 0;
    const pending = s.balance || 0;

    const matchesSearch =
      `${s.firstName} ${s.lastName || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    if (activeTab === "paid") return paid > 0 && matchesSearch;
    if (activeTab === "unpaid") return pending > 0 && matchesSearch;

    return false;
  });

  setFilteredStudents(filtered);
}, [students, activeTab, searchTerm]);

  /* ================= MONTH TOTALS (ONLY SELECTED MONTH) ================= */
const totalPaid = useMemo(
  () => filteredStudents.reduce((sum, s) => sum + (s.paidAmount || 0), 0),
  [filteredStudents]
);

const totalPending = useMemo(
  () => filteredStudents.reduce((sum, s) => sum + (s.balance || 0), 0),
  [filteredStudents]
);
  /* ================= EXPORT ================= */
const exportToExcel = () => {

  console.log("Filtered Students:", filteredStudents);

  if (!filteredStudents || filteredStudents.length === 0) {
    console.log("No students found for export");
    return;
  }

  const headers = [
    "Sr No",
    "Admission No",
    "Roll No",
    "Student Name",
    "Father Name",
    "Class",
    "Phone",
    "Paid Amount",
    "Tuition Pending",
    "Bus Pending",
    "Total Pending",
    "Status",
    "Payment Date"
  ];

  const rows = filteredStudents.map((s, i) => {
    console.log("Student Row:", s);   // 👈 Debug each student

    return [
      i + 1,
      s.admissionId,
      s.rollNo,
      `${s.firstName} ${s.lastName}`,
      s.fatherName,
      s.className,
      s.phoneNo,
      s.paidAmount,
      s.tuitionPending,
      s.busPending,
      s.balance,
      s.status,
      s.date
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Fee Report");

  XLSX.writeFile(wb, "Fee_Report.xlsx");
};

  /* ================= PRINT ================= */
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const newWindow = window.open("", "", "width=900,height=650");

    newWindow.document.write(`
      <html>
        <head>
          <title>Fee Report</title>
          <style>
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align:center; }
            th { background: #f3f4f6; }
          </style>
        </head>
        <body>
          <h2 style="text-align:center;">
            Fee Report - ${months[selectedMonth - 1]} ${selectedYear}
          </h2>
          ${printContents}
          <div style="margin-top:15px;">
            <h3>Total Paid: ₹${totalPaid}</h3>
            <h3>Total Pending: ₹${totalPending}</h3>
            <h4>Total Students: ${students.length}</h4>
          </div>
        </body>
      </html>
    `);

    newWindow.document.close();
    newWindow.print();
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-6">
        <h2 className="text-2xl font-bold mb-6 text-gray-700">
          Fee Report by Class
        </h2>

        {/* SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-green-700 font-semibold">Collected This Month</h4>
            <p className="text-2xl font-bold text-green-800">₹{totalPaid}</p>
          </div>
          <div className="bg-red-100 p-4 rounded">
            <h4 className="text-red-700 font-semibold">Pending This Month</h4>
            <p className="text-2xl font-bold text-red-800">₹{totalPending}</p>
          </div>
        </div>

        {/* FILTERS */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(+e.target.value)}
            className="border rounded px-3 py-2"
          >
            {months.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={e => setSelectedYear(+e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option>{today.getFullYear()}</option>
            <option>{today.getFullYear() - 1}</option>
          </select>

          <select
            value={selectedClass}
            onChange={e => setSelectedClass(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Class</option>
            {classes.map(c => (
              <option key={c._id} value={c.className}>{c.className}</option>
            ))}
          </select>

          {/* <select
            value={selectedSection}
            onChange={e => setSelectedSection(e.target.value)}
            disabled={!selectedClass}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Section</option>
            {sections.map(s => (
              <option key={s._id} value={s.sectionName}>{s.sectionName}</option>
            ))}
          </select> */}

          <input
            type="text"
            placeholder="Search student..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>

        {/* TABS */}
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setActiveTab("paid")}
            className={`px-4 py-2 rounded ${activeTab === "paid" ? "bg-green-600 text-white" : "bg-gray-200"}`}
          >
            Paid
          </button>
          <button
            onClick={() => setActiveTab("unpaid")}
            className={`px-4 py-2 rounded ${activeTab === "unpaid" ? "bg-red-600 text-white" : "bg-gray-200"}`}
          >
            Pending
          </button>
        </div>

        {/* ACTIONS */}
        <div className="flex justify-end gap-4 mb-4">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded">Export Excel</button>
          <button onClick={handlePrint} className="bg-blue-600 text-white px-4 py-2 rounded">Print</button>
        </div>

        {/* TABLE */}
        <div ref={printRef} className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-200">
              <tr>
                <th className="border px-2 py-2">Sr</th>
                <th className="border px-2 py-2">Name</th>
                <th className="border px-2 py-2">Class</th>
                <th className="border px-2 py-2">Paid Amount</th>
                <th className="border px-2 py-2">Tuition Pending</th>
                <th className="border px-2 py-2">Bus Pending</th>
                <th className="border px-2 py-2">Total Pending</th>
                <th className="border px-2 py-2">{activeTab === "paid" ? "Payment Date" : "Status"}</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length ? (
                filteredStudents.map((s, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-2">{i + 1}</td>
                    <td className="border px-2 py-2">{s.firstName} {s.lastName || ""}</td>
                    <td className="border px-2 py-2">{s.className}</td>
                    <td className="border px-2 py-2 text-green-700 font-semibold">₹{s.paidAmount || 0}</td>
                    <td className="border px-2 py-2">₹{s.tuitionPending || 0}</td>
                    <td className="border px-2 py-2">₹{s.busPending || 0}</td>
                    <td className="border px-2 py-2 font-semibold">₹{s.balance || 0}</td>
                    <td className="border px-2 py-2 font-semibold">
                      {activeTab === "paid" ? s.date || "—" : s.status || "unpaid"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-6 text-gray-500">No records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeeReportsByClass;